// Backs the "why did you remove this recommended session?" flow (Asana UNB-599).
// Owns two things: (1) is a given session assistant-recommended, and (2)
// capturing a submitted response to PostHog + D1. The gate itself lives in
// useScheduleAction (the one shared add/remove handler), so the modal fires from
// every surface — calendar, list, drawer.
//
// This used to stop asking after two consecutive dismissals, persisted in
// localStorage. Steph asked for the prompt on every removal instead (UNB-622),
// judging it not annoying enough to warrant going quiet — and the old behaviour
// meant a user who dismissed twice early silently stopped contributing removal
// data for the rest of the event. Restoring the cap means restoring the streak
// counter here plus its check in useScheduleAction.

export const useRemoveFeedback = () => {
    const { user } = useUserSession()

    // Recommended = placed by the Schedule Builder OR added from Find a Session
    // (server marks both; see getRecommendedIds). Manually-added sessions are
    // absent from this list and are removed without a prompt.
    const isRecommended = (uniqueId) =>
        !!uniqueId && (user.value?.scheduledRecommended || []).includes(uniqueId)

    // The user submitted the survey — fan the response out to PostHog + D1. Both
    // are best-effort and never throw back into the removal flow.
    const recordSubmit = async ({ session, reasons = [], otherText = '' }) => {
        captureSurvey(reasons, otherText)
        try {
            await $fetch('/api/v1/schedule/feedback', {
                method: 'POST',
                body: {
                    session_id: session?.unique_id,
                    reasons,
                    other_text: otherText || null,
                },
            })
        } catch {
            // Durable copy is best-effort; PostHog still has the response.
        }
    }

    // Reuse the headless findASessionSurvey (type: api) that backs the Find a
    // Session thumbs — same survey/question id, tagged feedback_source:'removal'
    // so removal reasons stay separable from thumbs sentiment. Responses are the
    // choice LABELS; the free-text is appended as an explicit "Other: …" entry.
    const captureSurvey = (reasons, otherText) => {
        const { surveyId, questionId } = useRuntimeConfig().public.findASessionSurvey ?? {}
        if (!surveyId || !questionId) return

        const response = [...(reasons || [])]
        if (otherText && otherText.trim()) response.push(`Other: ${otherText.trim()}`)

        try {
            useNuxtApp()?.$clientPosthog?.capture?.('survey sent', {
                $survey_id: surveyId,
                [`$survey_response_${questionId}`]: response,
                $survey_submission_id: crypto.randomUUID(),
                feedback_source: 'removal',
            })
        } catch {
            // Telemetry is best-effort — never block a removal on PostHog.
        }
    }

    return { isRecommended, recordSubmit }
}
